import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Globe, KeyRound } from 'lucide-react';
import {
    Badge,
    Button,
    ConfirmDialog,
    CopyableId,
    EmptyState,
    ErrorState,
    Loading,
    Modal,
} from '@fiado/react-kit';
import {
    hasNoIpRestriction,
    integrationsApi,
    type Integration,
} from '../../../../api/endpoints/integrations';
import { handleApiError } from '../../../../api/errorMapping';
import { Gate } from '../../../../components/permissions/Gate';
import { formatDateMx } from '../../../../lib/dateHelpers';
import { integrationKeysKey, useIntegrationKeys } from '../../../../hooks/useIntegrations';
import { IssuedSecretModal } from './IssuedSecretModal';

interface Props {
    tenantId: string;
    integration: Integration | null;
    onClose: () => void;
}

/** Lo que tarda una revocación en surtir efecto: el TTL del caché de filas del authorizer W5. */
const REVOCATION_DELAY = 'Puede tardar hasta 30 segundos en surtir efecto.';

/** Cuántas llaves activas admite una integración. Espejo de MAX_ACTIVE_KEYS del backend. */
const MAX_ACTIVE_KEYS = 2;

export function IntegrationKeysModal({ tenantId, integration, onClose }: Props) {
    const queryClient = useQueryClient();
    const [toRevoke, setToRevoke] = useState<string | null>(null);

    // El token en claro vive solo aquí. Al confirmar se limpia junto con la mutación que lo trajo.
    const [plaintextToken, setPlaintextToken] = useState<string | null>(null);

    const integrationId = integration?.integrationId;
    const { data: keys, isLoading, isError, refetch } = useIntegrationKeys(tenantId, integrationId);
    const activeCount = (keys ?? []).filter((k) => k.status === 'ACTIVE').length;

    const invalidate = () =>
        queryClient.invalidateQueries({
            queryKey: integrationKeysKey(tenantId, integrationId ?? ''),
        });

    const issueMut = useMutation({
        mutationFn: () =>
            integrationsApi.issueKey(tenantId, integrationId as string, undefined, {
                idempotencyKey: crypto.randomUUID(),
            }),
        onSuccess: async (issued) => {
            setPlaintextToken(issued.token);
            await invalidate();
        },
        onError: (error) => {
            handleApiError(error);
        },
    });

    const revokeMut = useMutation({
        mutationFn: (keyId: string) =>
            integrationsApi.revokeKey(tenantId, integrationId as string, keyId),
        onSuccess: async () => {
            toast.success(`Llave revocada. ${REVOCATION_DELAY}`);
            await invalidate();
        },
        onError: (error) => {
            handleApiError(error);
        },
    });

    // `issueMut.data` guarda el token: sin el reset queda vivo en el MutationCache.
    const clearSecret = () => {
        setPlaintextToken(null);
        issueMut.reset();
    };

    const openWithoutIpFilter = integration !== null && hasNoIpRestriction(integration);

    return (
        <>
            <Modal
                isOpen={integration !== null}
                onClose={onClose}
                title={`Llaves de ${integration?.displayName ?? ''}`}
                size="lg"
            >
                <div className="space-y-4">
                    {openWithoutIpFilter && (
                        <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                            <Globe className="h-4 w-4 shrink-0 text-amber-600" aria-hidden />
                            <span>
                                Esta integración <strong>no tiene restricción de IP</strong>: las
                                llaves que emitas van a funcionar desde cualquier lugar.
                            </span>
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-4">
                        <p className="text-sm text-muted-foreground">
                            Puede haber hasta <strong>{MAX_ACTIVE_KEYS} llaves activas</strong> a la
                            vez — es lo que permite rotar sin cortarle el servicio al socio: emites
                            la nueva, el socio migra, y hasta entonces revocas la vieja.
                        </p>
                        <Gate
                            permission="platform.integration.key.issue"
                            scope="TENANT"
                            scopeRef={tenantId}
                        >
                            <Button
                                onClick={() => {
                                    issueMut.mutate();
                                }}
                                isLoading={issueMut.isPending}
                                disabled={
                                    isError ||
                                    isLoading ||
                                    activeCount >= MAX_ACTIVE_KEYS ||
                                    integration?.status !== 'ACTIVE'
                                }
                                leftIcon={<KeyRound className="h-4 w-4" />}
                            >
                                Emitir llave
                            </Button>
                        </Gate>
                    </div>

                    {activeCount >= MAX_ACTIVE_KEYS && (
                        <p className="text-sm text-amber-600">
                            Ya hay {MAX_ACTIVE_KEYS} llaves activas. Revoca una para poder emitir
                            otra.
                        </p>
                    )}
                    {integration?.status === 'SUSPENDED' && (
                        <p className="text-sm text-amber-600">
                            La integración está suspendida: no se pueden emitir llaves hasta
                            reactivarla.
                        </p>
                    )}

                    {isLoading ? (
                        <Loading />
                    ) : isError ? (
                        <ErrorState
                            title="No se pudieron cargar las llaves"
                            message="No sabemos cuántas llaves tiene esta integración. Vuelve a intentar antes de emitir una nueva."
                            onRetry={() => void refetch()}
                            retryLabel="Reintentar"
                        />
                    ) : (keys ?? []).length === 0 ? (
                        <EmptyState
                            title="Todavía no tiene llaves"
                            description="Emite una para que el socio pueda autenticarse."
                        />
                    ) : (
                        <ul className="divide-y rounded-lg border">
                            {(keys ?? []).map((k) => (
                                <li
                                    key={k.keyId}
                                    className="flex flex-wrap items-center justify-between gap-3 p-3"
                                >
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            {/* El keyId no es secreto: viaja en claro dentro del token. */}
                                            <CopyableId value={k.keyId} label="ID de la llave" />
                                            <Badge
                                                variant={
                                                    k.status === 'ACTIVE' ? 'success' : 'default'
                                                }
                                            >
                                                {k.status === 'ACTIVE' ? 'Activa' : 'Revocada'}
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            Creada {formatDateMx(k.createdAt)}
                                            {' · '}
                                            {k.lastUsedAt
                                                ? `último uso ${formatDateMx(k.lastUsedAt)}`
                                                : 'nunca se usó'}
                                            {k.expiresAt
                                                ? ` · vence ${formatDateMx(k.expiresAt)}`
                                                : ''}
                                        </p>
                                    </div>
                                    {k.status === 'ACTIVE' && (
                                        <Gate
                                            permission="platform.integration.key.revoke"
                                            scope="TENANT"
                                            scopeRef={tenantId}
                                        >
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => {
                                                    setToRevoke(k.keyId);
                                                }}
                                            >
                                                Revocar
                                            </Button>
                                        </Gate>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </Modal>

            <ConfirmDialog
                open={toRevoke !== null}
                onOpenChange={(open) => {
                    if (!open) setToRevoke(null);
                }}
                title="¿Revocar esta llave?"
                description={`El socio que la esté usando va a empezar a recibir 401. ${REVOCATION_DELAY} No se puede deshacer: si te equivocas, hay que emitir una nueva y que el socio la cambie.`}
                confirmLabel="Revocar"
                destructive
                loading={revokeMut.isPending}
                onConfirm={() => {
                    if (toRevoke) revokeMut.mutate(toRevoke);
                    setToRevoke(null);
                }}
            />

            {/* La key remonta el modal para que la casilla no llegue tildada de la emisión anterior. */}
            <IssuedSecretModal
                key={issueMut.submittedAt}
                token={plaintextToken}
                onConfirm={clearSecret}
            />
        </>
    );
}
